// 2D canvas ship preview for the loadout screen: draws the same procedurally
// generated hull (seed + trait bias) that the 3D renderer builds, so players
// see their actual ship — and how traits reshape it — before launching.
// Pixelated, Lumen palette, no THREE dependency.

import { generateShipSpec, SHIP_SCALE, type ShipBias } from './ship-generator.js';

export function renderShipPreview(
  canvas: HTMLCanvasElement,
  seed: number,
  bias: ShipBias,
  weaponId: string
): void {
  const spec = generateShipSpec(seed, bias);
  const size = canvas.width;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.clearRect(0, 0, size, size);
  ctx.imageSmoothingEnabled = false;

  // Fit the hull (plus its nose spike) to the frame: measure the outline and
  // zoom so it fills ~80% of the canvas instead of floating in dead space.
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const p of spec.outline) {
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, p.y);
    maxY = Math.max(maxY, p.y);
  }
  maxY += 0.3; // room for the lance nose spike
  const hullW = Math.max(0.2, (maxX - minX) * SHIP_SCALE);
  const hullH = Math.max(0.2, (maxY - minY) * SHIP_SCALE);
  const zoom = (size * 0.8) / Math.max(hullW, hullH);
  const cx = size / 2 - ((minX + maxX) / 2) * SHIP_SCALE * zoom;
  const cy = size / 2 + ((minY + maxY) / 2) * SHIP_SCALE * zoom;
  const px = (x: number): number => cx + x * SHIP_SCALE * zoom;
  const py = (y: number): number => cy - y * SHIP_SCALE * zoom; // nose +Y up

  const path = (scale = 1): void => {
    ctx.beginPath();
    spec.outline.forEach((p, i) => {
      const x = cx + p.x * SHIP_SCALE * zoom * scale;
      const y = cy - p.y * SHIP_SCALE * zoom * scale;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.closePath();
  };

  // Dark outline behind the hull.
  path(1.18);
  ctx.fillStyle = 'rgba(26, 15, 0, 0.85)';
  ctx.fill();

  // Hull body with a warm emissive wash (per-seed hue, gold family).
  path();
  ctx.fillStyle = '#fff6dc';
  ctx.fill();
  ctx.save();
  path();
  ctx.clip();
  const hue = Math.round(spec.hue * 360);
  const grad = ctx.createLinearGradient(0, cy - size * 0.3, 0, cy + size * 0.3);
  grad.addColorStop(0, `hsla(${hue}, 85%, 62%, 0.55)`);
  grad.addColorStop(1, `hsla(${hue}, 85%, 40%, 0.15)`);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  ctx.restore();

  // Rim.
  path();
  ctx.strokeStyle = '#fff6dc';
  ctx.lineWidth = Math.max(1, size / 96);
  ctx.stroke();

  // Cockpit dot.
  ctx.beginPath();
  ctx.arc(px(spec.cockpit.x), py(spec.cockpit.y), Math.max(1.5, spec.cockpit.r * SHIP_SCALE * zoom), 0, Math.PI * 2);
  ctx.fillStyle = '#fff6dc';
  ctx.fill();

  // Engine bar.
  const ew = spec.engine.halfWidth * 2 * SHIP_SCALE * zoom;
  ctx.fillStyle = '#ffc45e';
  ctx.fillRect(cx - ew / 2, py(spec.engine.y) - size * 0.012, ew, size * 0.025);

  // Striker barrel studs.
  ctx.fillStyle = '#ffd36b';
  for (const s of spec.studs) {
    ctx.beginPath();
    ctx.arc(px(s.x), py(s.y), Math.max(1, s.r * SHIP_SCALE * zoom), 0, Math.PI * 2);
    ctx.fill();
  }

  // Trait tells — the build must read at a glance, not just through ±5%
  // shape deltas. SWIFT gets speed streaks, BULWARK an armor rim, STRIKER
  // hot studs at the wingtips.
  if (bias.swift > 0) {
    ctx.strokeStyle = 'rgba(159, 239, 255, 0.9)';
    ctx.lineWidth = Math.max(1.5, size / 64);
    const baseY = py(minY);
    for (let i = 0; i < bias.swift; i++) {
      const sx = cx + (i - (bias.swift - 1) / 2) * size * 0.07;
      ctx.beginPath();
      ctx.moveTo(sx, baseY + size * 0.02);
      ctx.lineTo(sx, baseY + size * (0.07 + 0.02 * (i % 2)));
      ctx.stroke();
    }
  }
  if (bias.bulwark > 0) {
    path(1.18 + bias.bulwark * 0.05);
    ctx.strokeStyle = 'rgba(77, 184, 255, 0.85)';
    ctx.lineWidth = Math.max(1.5, size / 64);
    ctx.stroke();
  }
  if (bias.striker > 0) {
    ctx.fillStyle = '#ff4d6b';
    const r = size * 0.016 + bias.striker * size * 0.006;
    // Wingtips = the outline's extreme x points.
    const left = spec.outline.reduce((a, p) => (p.x < a.x ? p : a));
    const right = spec.outline.reduce((a, p) => (p.x > a.x ? p : a));
    for (const tip of [left, right]) {
      ctx.beginPath();
      ctx.arc(px(tip.x), py(tip.y), r, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // Weapon mount (2D mirrors of the on-ship boon-visual mounts, oversized
  // so the choice is unmistakable).
  ctx.save();
  ctx.shadowBlur = Math.max(4, size / 24);
  if (weaponId === 'weapon.spear-jelly-lance') {
    const noseY = Math.max(...spec.outline.map((p) => p.y));
    ctx.fillStyle = '#ffd36b';
    ctx.shadowColor = '#ffd36b';
    ctx.fillRect(cx - size * 0.014, py(noseY) - size * 0.16, size * 0.028, size * 0.16);
    ctx.beginPath();
    ctx.arc(cx, py(noseY) - size * 0.16, size * 0.02, 0, Math.PI * 2);
    ctx.fill();
  } else if (weaponId === 'weapon.shard-scattergun') {
    ctx.fillStyle = '#ffd36b';
    ctx.shadowColor = '#ffd36b';
    // Twin chunky barrels flanking the nose.
    for (const side of [-1, 1]) {
      ctx.fillRect(cx + side * size * 0.075 - size * 0.025, cy - size * 0.09, size * 0.05, size * 0.14);
    }
  } else if (weaponId === 'weapon.seeker-swarm') {
    ctx.fillStyle = '#9fefff';
    ctx.shadowColor = '#9fefff';
    for (let i = 0; i < 4; i++) {
      ctx.beginPath();
      ctx.arc(
        cx + size * 0.1 + ((i % 2) * 2 - 1) * size * 0.035,
        cy - size * 0.1 + (i < 2 ? -1 : 1) * size * 0.035,
        size * 0.022,
        0,
        Math.PI * 2
      );
      ctx.fill();
    }
  }
  ctx.restore();
}
